import { useEffect } from "react";
import * as React from "react";
import { Link, useNavigate } from "react-router-dom";
import HomeIcon from "@mui/icons-material/Home";
import AdminIcon from "@mui/icons-material/AdminPanelSettings";
import MyPicksIcon from "@mui/icons-material/HowToVote";
import TrophyIcon from "@mui/icons-material/EmojiEvents";
import ListAllIcon from "@mui/icons-material/ListAlt";
import HistoryIcon from "@mui/icons-material/Schedule";
import { AppBar, Toolbar, IconButton, Typography, Avatar, Box, Button, Container, Menu, MenuItem, Tooltip } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import "./NavMenu.scss";
import { Identity } from "models/LoginInfo";
import UserService from "services/UserService";

const pages: { name: string; icon: JSX.Element; path: string; role?: string }[] = [
  { name: "Home", icon: <HomeIcon />, path: "/" },
  { name: "My Picks", icon: <MyPicksIcon />, path: "/picks" },
  { name: "All Picks", icon: <ListAllIcon />, path: "/picks/all" },
  { name: "Results", icon: <TrophyIcon />, path: "/results" },
  { name: "History", icon: <HistoryIcon />, path: "/history" },
  { name: "Admin", icon: <AdminIcon />, path: "/admin", role: "Admin" },
];

const settings = [{ name: "Profile", href: "/profile" }, { name: "Logout", href: "/logout" }];

export const NavMenu = () => {
  const [anchorElNav, setAnchorElNav] = React.useState<null | HTMLElement>(null);
  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(null);
  const [user, setUser] = React.useState<Identity>();
  const [roles, setRoles] = React.useState<string[]>([]);

  const navigate = useNavigate();

  useEffect(() => {
    const onAuthChange = (user: Identity | undefined) => {
      setUser(user);
      setRoles(user?.roles || []);
    };
    const user = UserService.getUserInfo();
    onAuthChange(user);
    UserService.authChangeObservable.subscribe(onAuthChange);
    return () => {
      UserService.authChangeObservable.unsubscribe(onAuthChange);
    };
  }, []);

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  };
  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleCloseUserMenu = (setting?: string) => {
    if (setting === "Logout") {
      UserService.logout();
      navigate("/login");
    }
    setAnchorElUser(null);
  };

  const stringToColor = (string: string) => {
    let hash = 0;
    let i;

    /* eslint-disable no-bitwise */
    for (i = 0; i < string.length; i += 1) {
      hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }

    let color = "#";

    for (i = 0; i < 3; i += 1) {
      const value = (hash >> (i * 8)) & 0xff;
      color += `00${value.toString(16)}`.slice(-2);
    }
    /* eslint-enable no-bitwise */

    return color;
  };

  const stringAvatar = (name: string) => {
    return {
      sx: {
        bgcolor: stringToColor(name),
      },
      children: name.split(" ").length > 1 ? `${name.split(" ")[0][0]}${name.split(" ")[1][0]}` : name[0],
    };
  };

  return (
    <AppBar position="static">
      <Container maxWidth="xl">
        <Toolbar disableGutters>
          <Typography
            variant="h6"
            noWrap
            component="a"
            href="/"
            sx={{
              mr: 2,
              display: { xs: "none", md: "flex" },
              fontFamily: "monospace",
              fontWeight: 700,
              letterSpacing: ".3rem",
              color: "inherit",
              textDecoration: "none",
            }}
          >
            PLAYOFF BRACKET POOL
          </Typography>

          <Box sx={{ flexGrow: 1, display: { xs: "flex", md: "none" } }}>
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleOpenNavMenu}
              color="inherit"
            >
              <MenuIcon />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorElNav}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
              keepMounted
              transformOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
              open={Boolean(anchorElNav)}
              onClose={handleCloseNavMenu}
              className="nav-menu"
              sx={{
                display: { xs: "block", md: "none" },
              }}
            >
              {pages
                .filter((c) => !c.role || roles.indexOf(c.role) !== -1)
                .map((page) => (
                  <MenuItem key={page.name} onClick={handleCloseNavMenu}>
                    {page.icon}
                    <Typography component="a" href={page.path} textAlign="center" className="nav-menu-item">
                      {page.name}
                    </Typography>
                  </MenuItem>
                ))}
            </Menu>
          </Box>

          <Typography
            variant="h5"
            noWrap
            component="a"
            href="/"
            sx={{
              mr: 2,
              display: { xs: "flex", md: "none" },
              flexGrow: 1,
              fontFamily: "monospace",
              fontWeight: 700,
              letterSpacing: ".3rem",
              color: "inherit",
              textDecoration: "none",
            }}
          >
            PLAYOFF BRACKET POOL
          </Typography>
          <Box sx={{ flexGrow: 1, justifyContent: "center", display: { xs: "none", md: "flex" } }}>
            {pages
              .filter((c) => !c.role || roles.indexOf(c.role) !== -1)
              .map((page) => (
                <Button key={page.name} component={Link} to={page.path} className="icon-button">
                  <div>{page.icon}</div>
                  <div>{page.name}</div>
                </Button>
              ))}
          </Box>

          <Box sx={{ flexGrow: 0 }}>
            {user && (
              <Tooltip
                title={
                  <div className="avatar__tooltip">
                    <div className="avatar__tooltip-content">Open Settings</div>
                  </div>
                }
              >
                <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                  <Avatar {...stringAvatar(user.teamName)} />
                </IconButton>
              </Tooltip>
            )}
            {!user && (
              <Tooltip title="Open settings">
                <Button component={Link} to="/login" sx={{ p: 0 }}>
                  Login
                </Button>
              </Tooltip>
            )}
            <Menu
              sx={{ mt: "45px" }}
              id="menu-appbar"
              anchorEl={anchorElUser}
              anchorOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              keepMounted
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              open={Boolean(anchorElUser)}
              onClose={() => handleCloseUserMenu()}
            >
              {settings.map((setting) => (
                <MenuItem key={setting.name} className="nav-menuitem">
                  <Typography textAlign="center" style={{}} component="a" href={setting.href}>{setting.name}</Typography>
                </MenuItem>
              ))}
            </Menu>
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
};
